// src/components/desk/contract/SectionRows.jsx — the SECTIONS as rows, in three places that
// must read the same (designer 2a "sections — bottom sheet", 5a the left rail):
//   • `SectionRows`   — `number · title · switch`; a lock and "always on" on the mandatory
//                       four; a CUSTOM badge with × on the agent's own; the greyed "PHOTOS"
//                       placeholder row for the annex (disabled, "soon"); "+ Add a section".
//                       Numbers close up live (sectionNumbers). The bottom sheet on a phone
//                       and the permanently visible left rail on a desktop mount THIS.
//   • `SectionsSheet` — the phone's bottom sheet around it (opened from the sticky bar).
//   • `SectionSlimRows` — the slim rows under the values on the paper ("3 · Payment of rent ·
//                       edited ↺") that open the clause editor for THAT section. Clause text
//                       is collapsed by default (round 1 decision 1): these rows are the only
//                       trace of it until one is tapped.
// `readOnly` — after the first signature: no switches, no add, no remove; the list still says
// what the document holds. Switches are drawn on the DARK sheet/rail; the slim rows on paper.

import { t, tOr } from '../../../lib/i18n.js'
import { Icon } from '../../Icon.jsx'
import { sectionEdited, sectionIssues, sectionNumbers, sectionTitle } from '../ContractSections.jsx'
import { INK_MUTED } from './paper.js'

// A switch that looks like one (the designer's toggle), on the dark rail. A real checkbox
// underneath, so the keyboard and screen readers get the same control.
function Switch({ on, disabled, onChange, title }) {
  return (
    <label className={`relative inline-flex items-center shrink-0 ${disabled ? 'opacity-50' : 'cursor-pointer'}`} title={title}>
      <input type="checkbox" role="switch" aria-checked={on} checked={on} disabled={disabled} className="peer sr-only" onChange={e => onChange(e.target.checked)} />
      <span className={`w-9 h-5 rounded-full transition-colors ${on ? 'bg-[#1F6F4F]' : 'bg-[#F7F5F0]/25'} peer-focus-visible:ring-2 peer-focus-visible:ring-[#F7F5F0]/60`} />
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#F7F5F0] transition-transform ${on ? 'translate-x-4' : ''}`} />
    </label>
  )
}

export function SectionRows({ sections, langs, issues, readOnly = false, onChange, onAdd, onOpen, activeKey = null, photosCount = null, onPhotos = null, photosOn = true, onPhotosToggle = null, photosRow = true }) {
  const numbers = sectionNumbers(sections)
  const setSection = (key, patch) => onChange(sections.map(s => s.key === key ? { ...s, ...patch } : s))
  const remove = key => onChange(sections.filter(s => s.key !== key))
  return (
    <div className="text-[13px]" data-testid="contract-sections">
      <ul className="divide-y divide-[#F7F5F0]/10">
        {sections.map(s => {
          const n = numbers.get(s.key)
          const edited = sectionEdited(s, langs)
          const drift = sectionIssues(s, issues).length > 0
          const openable = s.enabled && !s.mandatory && onOpen
          return (
            <li key={s.key} data-section-key={s.key} data-enabled={s.enabled ? '1' : '0'}
              className={`flex items-center gap-2 py-2 ${activeKey === s.key ? 'bg-[#F7F5F0]/5 -mx-2 px-2 rounded' : ''} ${s.enabled ? '' : 'opacity-45'}`}>
              <button type="button" className="flex-1 min-w-0 flex items-center gap-2 text-left disabled:cursor-default"
                disabled={!openable} onClick={() => openable && onOpen(s.key)}>
                <span className="w-5 shrink-0 text-right font-mono tabular-nums text-[11px] text-[#F7F5F0]/50">{n ?? '·'}</span>
                <span className={`min-w-0 break-words leading-tight ${s.enabled ? '' : 'line-through'}`}>{sectionTitle(s)}</span>
                {s.custom && <span className="shrink-0 font-mono text-[9px] tracking-[.12em] uppercase rounded border border-[#F7F5F0]/30 px-1 text-[#F7F5F0]/70">{t('desk_contract_section_custom')}</span>}
                {edited && <span className="shrink-0 text-[#1F6F4F] text-[11px]" title={t('desk_contract_clause_edited')}>↺</span>}
                {drift && <span className="shrink-0 text-red-300 text-[11px]" title={t('desk_contract_drift_title')}>!</span>}
              </button>
              {/* 🔒 A SWITCH THE SERVER WOULD REFUSE IS A CONTROL WITH NOTHING BEHIND IT
                  (diagnosis rule 6, and the owner's ruling of 2026-09-13: removability is
                  declared by the template, migration 1164). `removable` comes from the server,
                  so the rail cannot disagree with the write path — and an older server that
                  does not send it leaves `removable === undefined`, which keeps today's
                  behaviour rather than locking a document nobody can edit. */}
              {s.mandatory || s.removable === false ? (
                <span className="shrink-0 flex items-center gap-1 font-mono text-[10px] text-[#F7F5F0]/45"
                  title={t(s.policy === 'conditional' ? 'desk_contract_section_conditional' : 'desk_contract_section_mandatory')}>
                  <Icon name="lock" size={11} /> {t(s.policy === 'conditional' ? 'desk_contract_section_follows_answer' : 'desk_contract_section_always_on')}
                </span>
              ) : s.custom ? (
                <button type="button" className="shrink-0 text-[#F7F5F0]/60 hover:text-red-300 disabled:opacity-40 p-1"
                  title={t('desk_contract_section_remove')} disabled={readOnly} onClick={() => remove(s.key)}>
                  <Icon name="x" size={12} />
                </button>
              ) : (
                <Switch on={s.enabled} disabled={readOnly} onChange={on => setSection(s.key, { enabled: on })}
                  title={s.enabled ? t('desk_contract_section_on') : t('desk_contract_section_off')} />
              )}
            </li>
          )
        })}
        {/* The photo annex (B1257, built 2026-09-11): a live row that jumps to the annex card —
            on the composer the photos wait as pending and go up on create; on a contract
            they are the frozen annex. The count is the row's badge. */}
        {/* 📷 AND IT SWITCHES OFF LIKE THE REST (owner, 2026-09-12: "photos section must be also
            removable"). It was the one row in the list with no switch, which said it was
            mandatory — and it is not: plenty of contracts are signed without a condition annex.
            ⚠️ The switch is DISABLED once photographs exist, and says why. Turning it off would
            silently drop an artifact that is signed alongside the contract; removing the photos
            first is the same act, done where the consequence is visible. */}
        {/* a service agreement points to its lease instead of carrying photos (owner 2026-09-22) */}
        {photosRow && (
        <li className={`py-0 ${photosOn ? '' : 'opacity-50'}`} data-testid="contract-photos-row" data-on={photosOn ? '1' : '0'}>
          <div className="w-full flex items-center gap-2 py-2 rounded hover:bg-[#F7F5F0]/5">
            <button type="button" onClick={photosOn ? (onPhotos || undefined) : undefined} disabled={!onPhotos || !photosOn}
              title={t('desk_contract_photos_tip')}
              className="min-w-0 flex-1 flex items-center gap-2 text-left disabled:cursor-default">
              <span className="w-5 shrink-0 text-right font-mono text-[11px]">·</span>
              <span className="min-w-0 truncate flex items-center gap-1.5"><Icon name="camera" size={12} /> {t('desk_contract_photos_row')}</span>
              <span className="ml-auto shrink-0 font-mono text-[10px] text-[#F7F5F0]/60" data-testid="contract-photos-count">{photosCount ? photosCount : ''}</span>
            </button>
            {!readOnly && onPhotosToggle && (
              <Switch on={photosOn} disabled={!!photosCount}
                title={photosCount ? t('desk_contract_photos_has_photos') : t('desk_contract_photos_toggle')}
                onChange={v => onPhotosToggle(v)} />
            )}
          </div>
        </li>
        )}
      </ul>
      {!readOnly && (
        <button type="button" className="mt-2 w-full flex items-center gap-1.5 rounded-lg border border-dashed border-[#F7F5F0]/30 px-2 py-2 text-[13px] text-[#F7F5F0]/80 hover:border-[#F7F5F0]/60"
          onClick={() => onAdd(activeKey)}>
          <Icon name="plus" size={12} /> {t('desk_contract_section_add')}
        </button>
      )}
      {readOnly && <div className="mt-2 text-[11px] text-[#F7F5F0]/50 italic">{t('desk_contract_sections_locked')}</div>}
    </div>
  )
}

// The phone's bottom sheet: a scrim, the dark sheet from the bottom, the rows, a Done.
export function SectionsSheet({ open, onClose, children, on, total }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" data-testid="contract-sections-sheet">
      <button type="button" className="absolute inset-0 bg-black/50" aria-label={t('desk_contract_cancel')} onClick={onClose} />
      <div className="relative bg-[#14130F] text-[#F7F5F0] rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <span className="mx-auto absolute left-1/2 -translate-x-1/2 top-1.5 w-10 h-1 rounded-full bg-[#F7F5F0]/30" aria-hidden="true" />
          <span className="font-medium text-sm pt-1">{t('desk_contract_sections')}</span>
          <span className="font-mono text-[11px] text-[#F7F5F0]/55 pt-1">{on}/{total}</span>
          <button type="button" className="ml-auto text-sm text-[#F7F5F0]/80 px-2 py-1" onClick={onClose}>{t('desk_contract_done')}</button>
        </div>
        <div className="overflow-y-auto px-4 pb-6">{children}</div>
      </div>
    </div>
  )
}

// The slim rows on the paper, under the values: only the ENABLED, NON-mandatory sections
// (the mandatory four are generated or fixed text — there is nothing to open). A row names
// its number, its title, "edited ↺" when the text differs from the template, and "!" when
// a drift issue names one of its clauses.
// `inline` = { key, node }: on a desktop the open clause editor is drawn UNDER its own row.
export function SectionSlimRows({ sections, langs, issues, onOpen, activeKey = null, readOnly = false, inline = null }) {
  const numbers = sectionNumbers(sections)
  const rows = sections.filter(s => s.enabled && !s.mandatory)
  if (rows.length === 0) return null
  return (
    <ul className="divide-y divide-[#14130F]/10 border-y border-[#14130F]/10" data-testid="contract-section-rows">
      {rows.map(s => {
        const edited = sectionEdited(s, langs)
        const drift = sectionIssues(s, issues).length
        const n = numbers.get(s.key)
        return (
          <li key={s.key} data-section-key={s.key}>
            <button type="button" className={`w-full flex items-center gap-2 py-2.5 text-left text-[13px] ${activeKey === s.key ? 'bg-[#14130F]/5 -mx-2 px-2 rounded' : ''}`}
              onClick={() => onOpen(s.key)} data-testid="contract-section-row">
              <span className="w-5 shrink-0 text-right font-mono tabular-nums text-[11px] text-[#14130F]/50">{n ?? '·'}</span>
              <span className="min-w-0 break-words leading-tight">{sectionTitle(s)}</span>
              {s.custom && <span className="shrink-0 font-mono text-[9px] tracking-[.12em] uppercase rounded border border-[#14130F]/25 px-1 text-[#14130F]/60">{t('desk_contract_section_custom')}</span>}
              {edited && <span className={`shrink-0 text-[11px] ${INK_MUTED}`}>· {t('desk_contract_clause_edited')} <span className="text-[#1F6F4F]">↺</span></span>}
              {drift > 0 && <span className="shrink-0 text-[11px] text-red-700">· {tOr('desk_contract_drift_short', '!')}</span>}
              <span className="ml-auto shrink-0 text-[#14130F]/35">{readOnly ? <Icon name="eye" size={12} /> : <Icon name="pencil" size={12} />}</span>
            </button>
            {inline && inline.key === s.key && <div className="pb-3">{inline.node}</div>}
          </li>
        )
      })}
    </ul>
  )
}
